import { forwardRef, type InputHTMLAttributes, type MouseEvent } from 'react';

import { colors } from '../../colors';

import * as css from './Radio.css';

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
    /**
     * Primary label displayed next to the control.
     */
    label: string;

    /**
     * Applies secondary visual styling.
     */
    secondary?: boolean;

    /**
     * When true, the control expands to fill the available width.
     */
    fullWidth?: boolean;

    /**
     * Optional inline action button displayed when the control is checked.
     *
     * Useful for contextual actions such as "Edit" or "Configure".
     */
    action?: {
        /**
         * Label displayed inside the action button.
         */
        label: string;

        /**
         * When true, applies an active styling treatment to the action button.
         */
        active?: boolean;

        /**
         * Click handler for the action button.
         * Does not toggle the radio state.
         */
        onClick: (e: MouseEvent<HTMLButtonElement>) => void;
    };
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
    ({ label, secondary, fullWidth, checked, action, ...props }, ref) => {
        const handleActionClick = (e: MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();
            action?.onClick(e);
        };

        return (
            <div className={css.container({ secondary, fullWidth })}>
                <label className={css.label}>
                    <input type="checkbox" ref={ref} checked={checked} {...props} className={css.input} />
                    <span className={css.text}>{label}</span>
                    {checked && action && (
                        <button
                            type="button"
                            className={css.action}
                            onClick={handleActionClick}
                            style={action.active ? { background: colors.blue[500], color: colors.white } : undefined}
                        >
                            {action.label}
                        </button>
                    )}
                </label>
            </div>
        );
    },
);

Radio.displayName = 'Radio';
